import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { RadialShine } from 'src/game/components/shared/RadialShine';
import gsap from 'gsap';

export type ChestType = 'wood' | 'silver' | 'gold';
export type ChestState = 'close' | 'open';

const assets = {
  wood: {
    close: 'assets/ui/treasureExpress/popup/chest_wooden_closed.png',
    open: 'assets/ui/treasureExpress/popup/chest_wooden_open.png',
  },
  silver: {
    close: 'assets/ui/treasureExpress/popup/chest_silver_closed.png',
    open: 'assets/ui/treasureExpress/popup/chest_silver_open.png',
  },
  gold: {
    close: 'assets/ui/treasureExpress/popup/chest_gold_closed.png',
    open: 'assets/ui/treasureExpress/popup/chest_gold_open.png',
  },
} as const;

export type TreasureExpressChestOpts = {
  chestType?: ChestType;
  chestOpen?: boolean;
  chestShine?: boolean;
} & ViewOpts;

export class TreasureExpressChest extends View {
  private image: ImageScaleView;
  private shine: RadialShine;
  private chestType: ChestType = 'wood';
  private chestOpen = false;
  private chestShine = false;
  private timeline?: gsap.core.Timeline;

  constructor(opts: TreasureExpressChestOpts) {
    super({ ...opts });

    this.shine = new RadialShine({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      x: this.style.width / 2,
      y: this.style.height / 2,
      centerOnOrigin: true,
      scale: 1.8,
    });

    this.image = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      scaleMethod: 'contain',
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.style.height / 2,
    });

    this.addListener('ViewAdded', () => {
      this.updateAnimation();
    });

    this.addListener('ViewRemoved', () => {
      this.killAnimation();
    });

    this.updateOpts(opts);
  }

  public updateOpts(opts: TreasureExpressChestOpts): TreasureExpressChestOpts {
    super.updateOpts(opts);

    if (opts.chestType !== undefined) {
      this.chestType = opts.chestType;
    }

    if (opts.chestOpen !== undefined) {
      this.chestOpen = opts.chestOpen;
    }

    if (opts.chestShine !== undefined) {
      this.chestShine = opts.chestShine;
    }

    this.image.updateOpts({
      image: this.chestOpen
        ? assets[this.chestType].open
        : assets[this.chestType].close,
      width: this.style.width,
      height: this.style.height,
      scaleMethod: 'contain',
      anchorX: this.style.width * 0.5,
      anchorY: this.style.height * 0.7,
      x: this.style.width / 2,
      y: this.style.height / 2,
    });

    this.shine.updateOpts({
      visible: this.chestShine,
      width: this.style.width,
      height: this.style.height,
      x: this.style.width / 2,
      y: this.style.height / 2,
      centerOnOrigin: true,
    });

    this.updateAnimation();

    return this.style;
  }

  private updateAnimation() {
    this.killAnimation();
    // Open chest doesn't have animation
    if (this.chestOpen) {
      return;
    }
    // Animate chest differently if it's shining or not
    if (this.chestShine) {
      this.shineAnimation();
      this.lockedAnimation();
    }
  }

  private killAnimation() {
    this.timeline?.kill();
    gsap.killTweensOf(this.image.style);
    this.image.updateOpts({
      scale: 1,
      r: 0,
    });
  }

  private shineAnimation() {
    gsap.to(this.image.style, {
      duration: 0.5,
      scale: 1.1,
      repeat: -1,
      yoyo: true,
      ease: 'power1.inOut',
    });
  }

  private lockedAnimation() {
    this.timeline = gsap.timeline({ repeat: -1 });
    this.timeline.to(this.image.style, {
      duration: 0.1,
      r: 0.1,
      ease: 'sine.in',
    });
    this.timeline.to(this.image.style, {
      duration: 0.1,
      r: -0.1,
      yoyo: true,
      repeat: 3,
      ease: 'sine.inOut',
    });
    this.timeline.to(this.image.style, {
      duration: 0.1,
      r: 0,
      ease: 'sine.out',
    });
    this.timeline.to({}, { duration: 1 + Math.random() });
    this.timeline.seek(Math.random());
  }
}
